import React, { useState, useEffect } from 'react';

export default function BoqModal({ isOpen, onClose, addToCart, baseUrl = 'https://ebakasi.onrender.com' }) {
  const [clientName, setClientName] = useState('');
  const [contactMethod, setContactMethod] = useState('WhatsApp');
  const [contactValue, setContactValue] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [itemsRequestPayload, setItemsRequestPayload] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [file, setFile] = useState(null);
  const [parsedItems, setParsedItems] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingError, setRecordingError] = useState('');

  // NEW: DOCUMENT REVIEW OVERLAY STATE MATRIX
  const [generatedQuotationReview, setGeneratedQuotationReview] =
    useState(null);

  // Scroll to top when results are displayed
  useEffect(() => {
    if (parsedItems || generatedQuotationReview) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [parsedItems, generatedQuotationReview]);

  // Live microphone recording functions
  const startRecording = async () => {
    setRecordingError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        setFile(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Store the mediaRecorder instance to stop it later
      window.boqMediaRecorder = mediaRecorder;
    } catch (err) {
      setRecordingError('Microphone access denied. Please allow microphone access to record audio.');
      console.error('Error accessing microphone:', err);
    }
  };

  const stopRecording = () => {
    if (window.boqMediaRecorder && window.boqMediaRecorder.state === 'recording') {
      window.boqMediaRecorder.stop();
      setIsRecording(false);
    }
  };

  if (!isOpen) return null;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields before submitting
    if (!clientName || !contactValue || !deliveryLocation || !itemsRequestPayload) {
      alert('Please fill in all required fields: Client Name, Contact, Delivery Location, and BOQ Materials List.');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('ebakasiJwtToken');
      
      // Build payload with unmatchedItems array
      let payloadItems;
      if (parsedItems && parsedItems.unmatchedItems) {
        // Use parsed items from /parse response if available
        payloadItems = parsedItems.unmatchedItems;
      } else {
        // Fallback to raw lines as unmatched items
        payloadItems = itemsRequestPayload
          .split('\n')
          .filter((line) => line.trim() !== '')
          .map(l => ({ itemName: l.trim(), quantity: null, unit: '', notes: '' }));
      }
      
      const payload = JSON.stringify({
        rawText: itemsRequestPayload,
        unmatchedItems: payloadItems,
      });

      const res = await fetch(`${baseUrl}/api/boq/request`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({
          clientName,
          contactMethod,
          contactValue,
          deliveryLocation,
          itemsRequestPayload: payload,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        // Parse items request payload into a viewable matrix array for document synthesis
        const linesArray = itemsRequestPayload
          .split('\n')
          .filter((line) => line.trim() !== '');
        setGeneratedQuotationReview({
          ticketId: data.ticketId,
          timestamp: new Date(Date.now()).toLocaleString(),
          parsedLines: linesArray,
        });
      } else {
        alert('Platform validation gate error processing BOQ items.');
      }
    } catch (err) {
      alert('Network connection error synchronizing parameters.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleParseBoq = async () => {
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      if (itemsRequestPayload) {
        formData.append('text', itemsRequestPayload);
      }

      const res = await fetch(`${baseUrl}/api/boq/parse`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setParsedItems(data);
      } else {
        alert('Failed to parse BOQ: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Network error parsing BOQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMatchedToCart = (item) => {
    if (addToCart && item.matchedProduct) {
      // Resolve price: prefer lowest tier unitPrice if pricingTiers exists, else priceRetail
      let resolvedPrice = item.matchedProduct.priceRetail;
      if (item.matchedProduct.pricingTiers && item.matchedProduct.pricingTiers.length > 0) {
        const lowestTier = item.matchedProduct.pricingTiers.reduce((min, tier) => 
          (tier.unitPrice < min.unitPrice ? tier : min), item.matchedProduct.pricingTiers[0]);
        resolvedPrice = lowestTier.unitPrice;
      }

      addToCart({
        _id: item.matchedProduct.id,
        productId: item.matchedProduct.id,
        name: item.matchedProduct.name,
        priceRetail: resolvedPrice,
        quantity: parseInt(item.quantity) || 1,
        industrial_sector: item.matchedProduct.industrial_sector,
        asset_profile: item.matchedProduct.asset_profile,
      });
    }
  };

  const handleAddAllMatchedToCart = () => {
    if (parsedItems && parsedItems.matchedItems) {
      parsedItems.matchedItems.forEach(item => handleAddMatchedToCart(item));
    }
  };

  const handleSubmitUnmatched = async () => {
    if (!parsedItems || !parsedItems.unmatchedItems || parsedItems.unmatchedItems.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const unmatchedText = parsedItems.unmatchedItems
        .map(item => `${item.itemName} - ${item.quantity} (${item.unit}) - ${item.notes || ''}`)
        .join('\n');

      const res = await fetch('https://ebakasi.onrender.com/api/boq/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName,
          contactMethod,
          contactValue,
          deliveryLocation,
          itemsRequestPayload: unmatchedText,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        const linesArray = unmatchedText.split('\n').filter((line) => line.trim() !== '');
        setGeneratedQuotationReview({
          ticketId: data.ticketId || 'EBK-BOQ-' + Math.floor(100000 + Math.random() * 900000),
          timestamp: new Date(Date.now()).toLocaleString(),
          parsedLines: linesArray,
        });
        setParsedItems(null);
      } else {
        alert('Failed to submit unmatched items for quote');
      }
    } catch (err) {
      alert('Network error submitting unmatched items');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrintDocumentTrigger = () => {
    window.print();
  };

  const handleCloseFullModalReset = () => {
    setClientName('');
    setContactValue('');
    setDeliveryLocation('');
    setItemsRequestPayload('');
    setGeneratedQuotationReview(null);
    onClose();
  };
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(15,23,42,0.85)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 5000,
        overflowY: 'auto',
        padding: '20px 0',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-card, #1e293b)',
          padding: '25px',
          borderRadius: '14px',
          border: '1px solid #f59e0b',
          width: '90%',
          maxWidth: '550px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          boxSizing: 'border-box',
        }}
      >
        {/* STATE GATE 1: PRINT-READY DOCUMENT OVERVIEW IF BOQ IS GENERATED */}
        {generatedQuotationReview ? (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}
          >
            <div
              id="print-boq-invoice"
              style={{
                background: '#fff',
                color: '#0f172a',
                padding: '25px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontFamily: 'monospace',
              }}
            >
              <div
                style={{
                  borderBottom: '2px solid #0f172a',
                  paddingBottom: '10px',
                  marginBottom: '15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <h4
                    style={{
                      margin: 0,
                      fontSize: '18px',
                      fontWeight: '900',
                      color: '#ea580c',
                    }}
                  >
                    EBAKASI INDUSTRIAL UGANDA
                  </h4>
                  <small style={{ color: '#475569' }}>
                    Uganda's Sovereign Infrastructure Layer
                  </small>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <strong style={{ fontSize: '11px', display: 'block' }}>
                    OFFICIAL BOQ QUOTATION
                  </strong>
                  <span style={{ fontSize: '10px', color: '#64748b' }}>
                    {generatedQuotationReview.ticketId}
                  </span>
                </div>
              </div>

              <div
                style={{
                  fontSize: '11px',
                  marginBottom: '15px',
                  lineHeight: '1.4',
                }}
              >
                <strong>CLIENT:</strong> {clientName}
                <br />
                <strong>ROUTING TARGET:</strong> {contactValue} ({contactMethod}
                )<br />
                <strong>SITE LOGISTICS HUB:</strong> {deliveryLocation}
                <br />
                <strong>TIMESTAMP RECORD:</strong>{' '}
                {generatedQuotationReview.timestamp}
                <br />
                <strong>STATUS:</strong>{' '}
                <span style={{ color: '#16a34a', fontWeight: 'bold' }}>
                  VERIFIED BY EBAKASI CORE ENGINE
                </span>
              </div>

              <div
                style={{ borderTop: '1px solid #0f172a', paddingTop: '10px' }}
              >
                <strong
                  style={{
                    fontSize: '12px',
                    display: 'block',
                    marginBottom: '8px',
                  }}
                >
                  ITEMISED MATERIALS REQUIREMENT MATRIX:
                </strong>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '11px',
                  }}
                >
                  <tbody>
                    {generatedQuotationReview.parsedLines.map((line, idx) => (
                      <tr
                        key={idx}
                        style={{ borderBottom: '1px dashed #cbd5e1' }}
                      >
                        <td style={{ padding: '6px 0', color: '#334155' }}>
                          {line}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div
                style={{
                  borderTop: '2px solid #0f172a',
                  marginTop: '20px',
                  paddingTop: '10px',
                  fontSize: '10px',
                  color: '#64748b',
                  textAlign: 'center',
                  lineHeight: '1.4',
                }}
              >
                This document functions as an authoritative print-ready
                price query record.
                <br />
                Tier pricing applies at transaction checkout lines.
                <br />
                <strong>https://ebakasi.onrender.com</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button
                type="button"
                onClick={handlePrintDocumentTrigger}
                className="action-btn-pill"
                style={{ flex: '2', padding: '12px 0' }}
              >
                🖨️ Print / Save PDF
              </button>
              <button
                type="button"
                onClick={handleCloseFullModalReset}
                style={{
                  flex: '1',
                  padding: '12px 0',
                  backgroundColor: '#334155',
                  color: 'var(--text-primary, #fff)',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '700',
                  cursor: 'pointer',
                }}
              >
                Close Hub
              </button>
            </div>
          </div>
        ) : (
          /* STATE GATE 2: DATA-INTAKE ENTRY FORMS TERMINAL */
          <>
            <h3
              style={{
                margin: '0 0 5px 0',
                color: 'var(--text-primary, #fff)',
                fontSize: '18px',
                fontWeight: '800',
              }}
            >
              📋 REQUEST BOQ QUOTATION
            </h3>
            <p
              style={{
                color: '#94a3b8',
                fontSize: '12px',
                margin: '0 0 20px 0',
              }}
            >
              Submit your list of industrial hardware items. Ebakasi's engine
              compiles a print-ready price matrix with active B2B volume
              discounts.
            </p>

            <form
              onSubmit={handleFormSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <div>
                <label
                  style={{
                    fontSize: '12px',
                    color: '#cbd5e1',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  Corporate / Individual Buyer Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Lawrence Omoit"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'var(--bg-main, #0f172a)',
                    border: '1px solid var(--border-line, #334155)',
                    color: 'var(--text-primary, #fff)',
                    borderRadius: '6px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  gap: '10px',
                }}
              >
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      color: '#cbd5e1',
                      display: 'block',
                      marginBottom: '4px',
                    }}
                  >
                    Dispatch Track
                  </label>
                  <select
                    value={contactMethod}
                    onChange={(e) => setContactMethod(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: 'var(--bg-main, #0f172a)',
                      border: '1px solid var(--border-line, #334155)',
                      color: 'var(--text-primary, #fff)',
                      borderRadius: '6px',
                      outline: 'none',
                    }}
                  >
                    <option value="WhatsApp">💬 WhatsApp</option>
                    <option value="Email">✉️ Email</option>
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: '12px',
                      color: '#cbd5e1',
                      display: 'block',
                      marginBottom: '4px',
                    }}
                  >
                    Target Contact Endpoint Destination
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={
                      contactMethod === 'WhatsApp'
                        ? 'e.g. 0790206354'
                        : 'e.g. lawrence@gmail.com'
                    }
                    value={contactValue}
                    onChange={(e) => setContactValue(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: 'var(--bg-main, #0f172a)',
                      border: '1px solid var(--border-line, #334155)',
                      color: 'var(--text-primary, #fff)',
                      borderRadius: '6px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>

              <div>
                <label
                  style={{
                    fontSize: '12px',
                    color: '#cbd5e1',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  Delivery Site Location / District Hub
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Namanve Industrial Park, Mukono"
                  value={deliveryLocation}
                  onChange={(e) => setDeliveryLocation(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'var(--bg-main, #0f172a)',
                    border: '1px solid var(--border-line, #334155)',
                    color: 'var(--text-primary, #fff)',
                    borderRadius: '6px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '12px',
                    color: '#cbd5e1',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  Type or Paste Complete BOQ Materials List (Include Quantities)
                </label>
                <textarea
                  rows="4"
                  placeholder="e.g.&#10;1. Tororo Cement 50KG - 150 Bags&#10;2. Rebar Y12 Structural Steel - 40 Pcs&#10;3. PPR Pipe 25mm Hot Water - 15 Lengths"
                  value={itemsRequestPayload}
                  onChange={(e) => setItemsRequestPayload(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: 'var(--bg-main, #0f172a)',
                    border: '1px solid var(--border-line, #334155)',
                    color: 'var(--text-primary, #fff)',
                    borderRadius: '6px',
                    outline: 'none',
                    resize: 'none',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box',
                    fontSize: '12px',
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: '12px',
                    color: '#cbd5e1',
                    display: 'block',
                    marginBottom: '4px',
                  }}
                >
                  Upload BOQ Document (PDF/Image) or Record Voice Note
                </label>
                
                {/* Voice Recording Controls */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  {!isRecording ? (
                    <button
                      type="button"
                      onClick={startRecording}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#f59e0b',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      🎤 Record Voice
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={stopRecording}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      ⏹️ Stop Recording
                    </button>
                  )}
                  
                  {audioBlob && (
                    <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '700' }}>
                      ✓ Voice note recorded
                    </span>
                  )}
                </div>
                
                {recordingError && (
                  <div style={{ fontSize: '11px', color: '#ef4444', marginBottom: '8px' }}>
                    {recordingError}
                  </div>
                )}
                
                {/* File Upload for PDF/Image */}
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={(e) => {
                    setFile(e.target.files[0]);
                    setAudioBlob(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: 'var(--bg-main, #0f172a)',
                    border: '1px solid var(--border-line, #334155)',
                    color: 'var(--text-primary, #fff)',
                    borderRadius: '6px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="button"
                  onClick={handleParseBoq}
                  disabled={isSubmitting || (!itemsRequestPayload && !file)}
                  style={{
                    flex: '1',
                    padding: '12px 0',
                    backgroundColor: (!itemsRequestPayload && !file) ? '#334155' : '#f59e0b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '700',
                    cursor: (!itemsRequestPayload && !file) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSubmitting ? 'Parsing...' : '🔍 Parse BOQ'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    flex: '1',
                    padding: '12px 0',
                    backgroundColor: '#334155',
                    color: 'var(--text-primary, #fff)',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: '700',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>

              {/* Parsed Items Review */}
              {parsedItems && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: 'var(--bg-main, #0f172a)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-line, #334155)',
                }}>
                  <h4 style={{ margin: '0 0 15px 0', color: 'var(--text-primary, #fff)', fontSize: '14px' }}>
                    Parsed Results ({parsedItems.matchedCount} matched, {parsedItems.unmatchedCount} unmatched)
                  </h4>

                  {parsedItems.matchedItems.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}>
                          ✓ Matched Items
                        </span>
                        <button
                          onClick={handleAddAllMatchedToCart}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                          }}
                        >
                          Add All to Cart
                        </button>
                      </div>
                      {parsedItems.matchedItems.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '10px',
                            backgroundColor: 'var(--bg-card, #1e293b)',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <div>
                            <div style={{ color: 'var(--text-primary, #fff)', fontWeight: '600', fontSize: '12px' }}>
                              {item.itemName}
                            </div>
                            <div style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '11px' }}>
                              {item.quantity} {item.unit} - UGX {(() => {
                                let displayPrice = item.matchedProduct?.priceRetail || 0;
                                if (item.matchedProduct?.pricingTiers && item.matchedProduct.pricingTiers.length > 0) {
                                  const lowestTier = item.matchedProduct.pricingTiers.reduce((min, tier) => 
                                    (tier.unitPrice < min.unitPrice ? tier : min), item.matchedProduct.pricingTiers[0]);
                                  displayPrice = lowestTier.unitPrice;
                                }
                                return displayPrice.toLocaleString();
                              })()}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAddMatchedToCart(item)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#10b981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '600',
                              cursor: 'pointer',
                            }}
                          >
                            Add to Cart
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {parsedItems.unmatchedItems.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '12px' }}>
                          ⚠ Unmatched Items
                        </span>
                        <button
                          onClick={handleSubmitUnmatched}
                          disabled={isSubmitting}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isSubmitting ? 'Submitting...' : 'Request Quote'}
                        </button>
                      </div>
                      {parsedItems.unmatchedItems.map((item, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '10px',
                            backgroundColor: 'var(--bg-card, #1e293b)',
                            borderRadius: '6px',
                            marginBottom: '8px',
                          }}
                        >
                          <div style={{ color: 'var(--text-primary, #fff)', fontWeight: '600', fontSize: '12px' }}>
                            {item.itemName}
                          </div>
                          <div style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '11px' }}>
                            {item.quantity} {item.unit} - {item.status}
                          </div>
                          {item.notes && (
                            <div style={{ color: 'var(--text-secondary, #94a3b8)', fontSize: '10px', marginTop: '4px' }}>
                              {item.notes}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => setParsedItems(null)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      backgroundColor: '#334155',
                      color: 'var(--text-primary, #fff)',
                      border: 'none',
                      borderRadius: '6px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '15px',
                    }}
                  >
                    Back to Form
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="action-btn-pill"
                  style={{ flex: '2', padding: '12px 0' }}
                >
                  {isSubmitting
                    ? 'Processing BOQ Matrix...'
                    : 'Stream Official Price Sheet'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
